import { isDefined, isEmpty, getAny, remove, contains, all, forEach, isUndefined, isString, first, initArray, randomInteger, fold, isObject } from '../utils/functions.js';
import { randomId } from './randomId.js';
import { Rect, Point } from './Shapes.js';
import { Dictionary } from './Dictionary.js';
import { Queue } from './Queue.js';
import { deepExtend } from '../utils/deep-extend.js';
import { LayoutBase } from '../layout/LayoutBase.js';
import { LayoutState } from '../layout/LayoutState.js';

/**
 * Defines the node (vertex) of a Graph.
 */
class Node {
    constructor(id, shape) {
        /**
         * Holds all the links incident with the current node.
         * Do not use this property to manage the incoming links, use the appropriate add/remove methods instead.
         */
        this.links = [];
        /**
         * Holds the links from the current one to another Node .
         * Do not use this property to manage the incoming links, use the appropriate add/remove methods instead.
         */
        this.outgoing = [];
        /**
         * Holds the links from another Node to the current one.
         * Do not use this property to manage the incoming links, use the appropriate add/remove methods instead.
         */
        this.incoming = [];
        /**
         * Holds the weight of this Node.
         */
        this.weight = 1;
        /**
         * The payload of the node.
         *
         * @type {null}
         */
        this.data = null;
        this.type = 'Node';
        /**
         * Whether this is an injected node during the analysis or layout process.
         *
         * @type {boolean}
         */
        this.isVirtual = false;
        if (isDefined(id)) {
            this.id = id;
        }
        else {
            this.id = randomId();
        }
        if (isDefined(shape)) {
            this.associatedShape = shape;
            // transfer the shape's bounds to the runtime props
            const b = shape.bounds();
            this.width = b.width;
            this.height = b.height;
            this.x = b.x;
            this.y = b.y;
        }
        else {
            this.associatedShape = null;
        }
        this.shortForm = 'Node \'' + this.id + '\'';
    }
    /**
     * Returns whether this node has no links attached.
     */
    isIsolated() {
        return isEmpty(this.links);
    }
    /**
     * Gets or sets the bounding rectangle of this node.
     * This should be considered as runtime data, the property is not hotlinked to a SVG item.
     */
    bounds(r) {
        if (!isDefined(r)) {
            return new Rect(this.x, this.y, this.width, this.height);
        }
        this.x = r.x;
        this.y = r.y;
        this.width = r.width;
        this.height = r.height;
    }
    /**
     * Returns whether there is at least one link with the given (complementary) node. This can be either an
     * incoming or outgoing link.
     */
    isLinkedTo(node) {
        return getAny(this.links, (link) => {
            return link.getComplement(this) === node;
        });
    }
    /**
     * Gets the children of this node, defined as the adjacent nodes with a link from this node to the adjacent one.
     *
     * @returns {Array}
     */
    getChildren() {
        if (this.outgoing.length === 0) {
            return [];
        }
        const children = [];
        for (let i = 0, len = this.outgoing.length; i < len; i++) {
            const link = this.outgoing[i];
            children.push(link.getComplement(this));
        }
        return children;
    }
    /**
     * Gets the parents of this node, defined as the adjacent nodes with a link from the adjacent node to this one.
     *
     * @returns {Array}
     */
    getParents() {
        if (this.incoming.length === 0) {
            return [];
        }
        const parents = [];
        for (let i = 0, len = this.incoming.length; i < len; i++) {
            const link = this.incoming[i];
            parents.push(link.getComplement(this));
        }
        return parents;
    }
    /**
     * Returns a clone of the Node. Note that the identifier is not cloned since it's a different Node instance.
     *
     * @returns {Node}
     */
    clone() {
        const copy = new Node();
        if (isDefined(this.weight)) {
            copy.weight = this.weight;
        }
        if (isDefined(this.balance)) {
            copy.balance = this.balance;
        }
        if (isDefined(this.owner)) {
            copy.owner = this.owner;
        }
        copy.associatedShape = this.associatedShape;
        copy.x = this.x;
        copy.y = this.y;
        copy.width = this.width;
        copy.height = this.height;
        return copy;
    }
    /**
     * Returns whether there is a link from the current node to the given node.
     */
    adjacentTo(node) {
        return this.isLinkedTo(node) !== null;
    }
    /**
     * Removes the given link from the link collection this node owns.
     *
     * @param link
     */
    removeLink(link) {
        if (link.source === this) {
            remove(this.links, link);
            remove(this.outgoing, link);
            link.source = null;
        }
        if (link.target === this) {
            remove(this.links, link);
            remove(this.incoming, link);
            link.target = null;
        }
    }
    /**
     * Returns whether there is a (outgoing) link from the current node to the given one.
     */
    hasLinkTo(node) {
        return getAny(this.outgoing, function (link) {
            return link.target === node;
        });
    }
    /**
     * Returns the degree of this node, i.e. the sum of incoming and outgoing links.
     */
    degree() {
        return this.links.length;
    }
    /**
     * Returns whether this node is either the source or the target of the given link.
     */
    incidentWith(link) {
        return contains(this.links, link);
    }
    /**
     * Returns the links between this node and the given one.
     */
    getLinksWith(node) {
        return all(this.links, function (link) {
            return link.getComplement(this) === node;
        }, this);
    }
    /**
     * Returns the nodes (either parent or child) which are linked to the current one.
     */
    getNeighbors() {
        const neighbors = [];
        forEach(this.incoming, function (e) {
            neighbors.push(e.getComplement(this));
        }, this);
        forEach(this.outgoing, function (e) {
            neighbors.push(e.getComplement(this));
        }, this);
        return neighbors;
    }
}
/**
 * Defines a directed link (edge, connection) of a Graph.
 */
class Link {
    constructor(source, target, id, connection) {
        if (isUndefined(source)) {
            throw new Error('The source of the new link is not set.');
        }
        if (isUndefined(target)) {
            throw new Error('The target of the new link is not set.');
        }
        let sourceFound, targetFound;
        if (isString(source)) {
            sourceFound = new Node(source);
        }
        else {
            sourceFound = source;
        }
        if (isString(target)) {
            targetFound = new Node(target);
        }
        else {
            targetFound = target;
        }
        this.source = sourceFound;
        this.target = targetFound;
        this.source.links.push(this);
        this.target.links.push(this);
        this.source.outgoing.push(this);
        this.target.incoming.push(this);
        if (isDefined(id)) {
            this.id = id;
        }
        else {
            this.id = randomId();
        }
        if (isDefined(connection)) {
            this.associatedConnection = connection;
        }
        else {
            this.associatedConnection = null;
        }
        this.type = 'Link';
        this.shortForm = 'Link \'' + this.source.id + '->' + this.target.id + '\'';
    }
    /**
     * Returns the complementary node of the given one, if any.
     */
    getComplement(node) {
        if (this.source !== node && this.target !== node) {
            throw new Error('The given node is not incident with this link.');
        }
        return this.source === node ? this.target : this.source;
    }
    /**
     * Returns the overlap of the current link with the given one, if any.
     */
    getCommonNode(link) {
        if (this.source === link.source || this.source === link.target) {
            return this.source;
        }
        if (this.target === link.source || this.target === link.target) {
            return this.target;
        }
        return null;
    }
    /**
     * Returns whether the current link is bridging the given nodes.
     */
    isBridging(v1, v2) {
        return this.source === v1 && this.target === v2 || this.source === v2 && this.target === v1;
    }
    /**
     * Returns the source and target of this link as a tuple.
     */
    getNodes() {
        return [this.source, this.target];
    }
    /**
     * Returns whether the given node is either the source or the target of the current link.
     */
    incidentWith(node) {
        return this.source === node || this.target === node;
    }
    /**
     * Returns whether the given link is a continuation of the current one. This can be both
     * via an incoming or outgoing link.
     */
    adjacentTo(link) {
        return contains(this.source.links, link) || contains(this.target.links, link);
    }
    /**
     * Changes the source-node of this link.
     */
    changeSource(node) {
        remove(this.source.links, this);
        remove(this.source.outgoing, this);
        node.links.push(this);
        node.outgoing.push(this);
        this.source = node;
    }
    /**
     * Changes the target-node of this link.
     *
     * @param node
     */
    changeTarget(node) {
        remove(this.target.links, this);
        remove(this.target.incoming, this);
        node.links.push(this);
        node.incoming.push(this);
        this.target = node;
    }
    /**
     * Changes both the source and the target nodes of this link.
     */
    changesNodes(v, w) {
        if (this.source === v) {
            this.changeSource(w);
        }
        else if (this.target === v) {
            this.changeTarget(w);
        }
    }
    /**
     * Reverses the direction of this link.
     */
    reverse() {
        const oldSource = this.source;
        const oldTarget = this.target;
        this.source = oldTarget;
        remove(oldSource.outgoing, this);
        this.source.outgoing.push(this);
        this.target = oldSource;
        remove(oldTarget.incoming, this);
        this.target.incoming.push(this);
        return this;
    }
    /**
     * Ensures that the given target defines the endpoint of this link.
     */
    directTo(target) {
        if (this.source !== target && this.target !== target) {
            throw new Error('The given node is not incident with this link.');
        }
        if (this.target !== target) {
            this.reverse();
        }
    }
    /**
     * Returns a reversed clone of this link.
     */
    createReverseEdge() {
        const r = this.clone();
        r.reverse();
        r.reversed = true;
        return r;
    }
    /**
     * Returns a clone of this link.
     */
    clone() {
        const clone = new Link(this.source, this.target);
        return clone;
    }
}
/* --------------Graph structure---------------------------------*/
/**
 * Defines a directed graph structure.
 * Note that the incidence structure resides in the nodes through the incoming and outgoing links collection, rahter than
 * inside the Graph.
 */
class Graph {
    constructor(idOrDiagram) {
        /**
         * The links or edge collection of this Graph.
         *
         * @type {Array}
         */
        this.links = [];
        /**
         * The node or vertex collection of this Graph.
         *
         * @type {Array}
         */
        this.nodes = [];
        this._nodeMap = new Dictionary();
        /**
         * The optional reference to the Diagram on which this Graph is based.
         *
         * @type {null}
         */
        this.diagram = null;
        /**
         * The root of this Graph. If not set explicitly the first Node with zero incoming links will be taken.
         *
         * @type {null}
         * @private
         */
        this._root = null;
        /**
         * The bounds of this graph if the nodes have spatial extension defined.
         *
         * @type {Rect}
         */
        this.bounds = new Rect();
        // keeps track whether the children & parents have been created
        this._hasCachedRelationships = false;
        this.type = 'Graph';
        this.componentIndex = 0;
        if (isDefined(idOrDiagram)) {
            if (isString(idOrDiagram)) {
                this.id = idOrDiagram;
            }
            else {
                this.diagram = idOrDiagram;
                this.id = idOrDiagram.id;
            }
        }
        else {
            this.id = randomId();
        }
    }
    /**
     * Caches the relational information of parents and children in the 'parents' and 'children'
     * properties.
     *
     * @param forceRebuild If set to true the relational info will be rebuild even if already present.
     */
    cacheRelationships(forceRebuild) {
        if (isUndefined(forceRebuild)) {
            forceRebuild = false;
        }
        if (this._hasCachedRelationships && !forceRebuild) {
            return;
        }
        for (let i = 0, len = this.nodes.length; i < len; i++) {
            const node = this.nodes[i];
            node.children = this.getChildren(node);
            node.parents = this.getParents(node);
        }
        this._hasCachedRelationships = true;
    }
    /**
     * Assigns tree-levels to the nodes assuming this is a tree graph.
     * If not connected or not a tree the process will succeed but
     * will have little meaning.
     *
     * @param startNode The node from where the level numbering starts, usually the root of the tree.
     * @param visited The collection of visited nodes.
     * @param offset The offset or starting counter of the level info.
     */
    assignLevels(startNode, offset, visited) {
        if (!startNode) {
            throw new Error('Start node not specified.');
        }
        if (isUndefined(offset)) {
            offset = 0;
        }
        // if not done before, cache the parents and children
        this.cacheRelationships();
        if (isUndefined(visited)) {
            visited = new Dictionary();
            forEach(this.nodes, function (n) {
                visited.add(n, false);
            });
        }
        visited.set(startNode, true);
        startNode.level = offset;
        const children = startNode.children;
        for (let i = 0, len = children.length; i < len; i++) {
            const child = children[i];
            if (!child || visited.get(child)) {
                continue;
            }
            this.assignLevels(child, offset + 1, visited);
        }
    }
    /**
     * Gets or set the root of this graph.
     * If not set explicitly the first Node with zero incoming links will be taken.
     *
     * @param value
     * @returns {*}
     */
    root(value) {
        if (isUndefined(value)) {
            if (!this._root) {
                // TODO: better to use the longest path for the most probable root?
                const found = first(this.nodes, function (n) {
                    return n.incoming.length === 0;
                });
                if (found) {
                    return found;
                }
                return first(this.nodes);
            }
            else {
                return this._root;
            }
        }
        else {
            this._root = value;
        }
    }
    /**
     * Returns the connected components of this graph.
     * Note that the returned graphs are made up of the nodes and links of this graph, i.e. a pointer to the items of this graph.
     * If you alter the items of the components you'll alter the original graph and vice versa.
     *
     * @returns {Array}
     */
    getConnectedComponents() {
        this.componentIndex = 0;
        this.setItemIndices();
        const componentId = initArray(this.nodes.length, -1);
        for (let v = 0; v < this.nodes.length; v++) {
            if (componentId[v] === -1) {
                this._collectConnectedNodes(componentId, v);
                this.componentIndex++;
            }
        }
        const components = [];
        let i;
        for (i = 0; i < this.componentIndex; ++i) {
            components[i] = new Graph();
        }
        for (i = 0; i < componentId.length; ++i) {
            const graph = components[componentId[i]];
            graph.addNodeAndOutgoings(this.nodes[i]);
        }
        // sorting the components in decreasing order of node count
        components.sort(function (a, b) {
            return b.nodes.length - a.nodes.length;
        });
        return components;
    }
    _collectConnectedNodes(setIds, nodeIndex) {
        setIds[nodeIndex] = this.componentIndex; // part of the current component
        const node = this.nodes[nodeIndex];
        forEach(node.links, function (link) {
            const next = link.getComplement(node);
            const nextId = next.index;
            if (setIds[nextId] === -1) {
                this._collectConnectedNodes(setIds, nextId);
            }
        }, this);
    }
    /**
     * Calculates the bounds of this Graph if the Nodes have spatial dimensions defined.
     *
     * @returns {Rect}
     */
    calcBounds() {
        if (this.isEmpty()) {
            this.bounds = new Rect();
            return this.bounds;
        }
        let b = null;
        for (let i = 0, len = this.nodes.length; i < len; i++) {
            const node = this.nodes[i];
            if (!b) {
                b = node.bounds();
            }
            else {
                b = b.union(node.bounds());
            }
        }
        this.bounds = b;
        return this.bounds;
    }
    /**
     * Creates a spanning tree for the current graph.
     * Important: this will not return a spanning forest if the graph is disconnected.
     * Prim's algorithm  finds a minimum-cost spanning tree of an edge-weighted, connected, undirected graph;
     * see http://en.wikipedia.org/wiki/Prim%27s_algorithm .
     *
     * @param root The root of the spanning tree.
     * @returns {Graph}
     */
    getSpanningTree(root) {
        const tree = new Graph();
        const map = new Dictionary();
        let source, target;
        const rootClone = root.clone();
        tree.root(rootClone);
        rootClone.level = 0;
        rootClone.id = root.id;
        map.add(root, tree.root());
        root.level = 0;
        const visited = [];
        const remaining = [];
        tree._addNode(tree.root());
        visited.push(root);
        remaining.push(root);
        let levelCount = 1;
        while (remaining.length > 0) {
            const next = remaining.pop();
            for (let ni = 0; ni < next.links.length; ni++) {
                const link = next.links[ni];
                const cn = link.getComplement(next);
                if (contains(visited, cn)) {
                    continue;
                }
                cn.level = next.level + 1;
                if (levelCount < cn.level + 1) {
                    levelCount = cn.level + 1;
                }
                if (!contains(remaining, cn)) {
                    remaining.push(cn);
                }
                if (!contains(visited, cn)) {
                    visited.push(cn);
                }
                if (map.containsKey(next)) {
                    source = map.get(next);
                }
                else {
                    source = next.clone();
                    source.level = next.level;
                    source.id = next.id;
                    map.add(next, source);
                }
                if (map.containsKey(cn)) {
                    target = map.get(cn);
                }
                else {
                    target = cn.clone();
                    target.level = cn.level;
                    target.id = cn.id;
                    map.add(cn, target);
                }
                const newLink = new Link(source, target);
                tree.addLink(newLink);
            }
        }
        const treeLevels = [];
        for (let i = 0; i < levelCount; i++) {
            treeLevels.push([]);
        }
        forEach(tree.nodes, function (node) {
            treeLevels[node.level].push(node);
        });
        tree.treeLevels = treeLevels;
        tree.cacheRelationships();
        return tree;
    }
    /**
     * Returns a random node in this graph.
     *
     * @param excludedNodes The collection of nodes which should not be considered.
     * @param incidenceLessThan The maximum degree or incidence the random node should have.
     * @returns {*}
     */
    takeRandomNode(excludedNodes, incidenceLessThan) {
        if (isUndefined(excludedNodes)) {
            excludedNodes = [];
        }
        if (isUndefined(incidenceLessThan)) {
            incidenceLessThan = 4;
        }
        if (this.nodes.length === 0) {
            return null;
        }
        if (this.nodes.length === 1) {
            return contains(excludedNodes, this.nodes[0]) ? null : this.nodes[0];
        }
        const pool = this.nodes.filter(function (node) {
            return !contains(excludedNodes, node) && node.degree() <= incidenceLessThan;
        });
        if (isEmpty(pool)) {
            return null;
        }
        return pool[randomInteger(0, pool.length)];
    }
    /**
     * Returns whether this is an empty graph.
     */
    isEmpty() {
        return isEmpty(this.nodes);
    }
    /**
     * Checks whether the endpoints of the links are all in the nodes collection.
     */
    isHealthy() {
        return all(this.links, function (link) {
            return contains(this.nodes, link.source) && contains(this.nodes, link.target);
        }, this);
    }
    /**
     * Gets the parents of this node, defined as the adjacent nodes with a link from the adjacent node to this one.
     *
     * @returns {Array}
     */
    getParents(n) {
        if (!this.hasNode(n)) {
            throw new Error('The given node is not part of this graph.');
        }
        return n.getParents();
    }
    /**
     * Gets the children of this node, defined as the adjacent nodes with a link from this node to the adjacent one.
     *
     * @returns {Array}
     */
    getChildren(n) {
        if (!this.hasNode(n)) {
            throw new Error('The given node is not part of this graph.');
        }
        return n.getChildren();
    }
    /**
     * Adds a new link to the graph between the given nodes.
     */
    addLink(sourceOrLink, target, owner) {
        if (isUndefined(sourceOrLink)) {
            throw new Error('The source of the link is not defined.');
        }
        if (isUndefined(target)) {
            // can only be undefined if the first one is a Link
            if (isDefined(sourceOrLink.type) && sourceOrLink.type === 'Link') {
                this.addExistingLink(sourceOrLink);
                return;
            }
            else {
                throw new Error('The target of the link is not defined.');
            }
        }
        let foundSource = this.getNode(sourceOrLink);
        if (isUndefined(foundSource)) {
            foundSource = this.addNode(sourceOrLink);
        }
        let foundTarget = this.getNode(target);
        if (isUndefined(foundTarget)) {
            foundTarget = this.addNode(target);
        }
        const newLink = new Link(foundSource, foundTarget);
        if (isDefined(owner)) {
            newLink.owner = owner;
        }
        /* newLink.source.outgoing.push(newLink);
            newLink.source.links.push(newLink);
            newLink.target.incoming.push(newLink);
            newLink.target.links.push(newLink);*/
        this.links.push(newLink);
        return newLink;
    }
    /**
     * Removes all the links in this graph.
     */
    removeAllLinks() {
        while (this.links.length > 0) {
            const link = this.links[0];
            this.removeLink(link);
        }
    }
    /**
     * Adds the given link to the current graph.
     */
    addExistingLink(link) {
        if (this.hasLink(link)) {
            return;
        }
        this.links.push(link);
        if (this.hasNode(link.source.id)) {
            // priority to the existing node with the id even if other props are different
            const s = this.getNode(link.source.id);
            link.changeSource(s);
        }
        else {
            this.addNode(link.source);
        }
        if (this.hasNode(link.target.id)) {
            const t = this.getNode(link.target.id);
            link.changeTarget(t);
        }
        else {
            this.addNode(link.target);
        }
        /*  if (!link.source.outgoing.contains(link)) {
            link.source.outgoing.push(link);
            }
            if (!link.source.links.contains(link)) {
            link.source.links.push(link);
            }
            if (!link.target.incoming.contains(link)) {
            link.target.incoming.push(link);
            }
            if (!link.target.links.contains(link)) {
            link.target.links.push(link);
            }*/
    }
    /**
     * Returns whether the given identifier or Link is part of this graph.
     *
     * @param linkOrId An identifier or a Link object.
     * @returns {*}
     */
    hasLink(linkOrId) {
        if (isString(linkOrId)) {
            return getAny(this.links, function (link) {
                return link.id === linkOrId;
            });
        }
        if (linkOrId.type === 'Link') {
            return contains(this.links, linkOrId);
        }
        throw new Error('The given object is neither an identifier nor a Link.');
    }
    /**
     * Gets the node with the specified Id or null if not part of this graph.
     */
    getNode(nodeOrId) {
        const id = nodeOrId.id || nodeOrId;
        if (this._nodeMap.containsKey(id)) {
            return this._nodeMap.get(id);
        }
    }
    /**
     * Returns whether the given node or node Id is part of this graph.
     */
    hasNode(nodeOrId) {
        const id = nodeOrId.id || nodeOrId;
        return this._nodeMap.containsKey(id);
    }
    _addNode(node) {
        this.nodes.push(node);
        this._nodeMap.add(node.id, node);
    }
    _removeNode(node) {
        remove(this.nodes, node);
        this._nodeMap.remove(node.id);
    }
    /**
     * Removes the given node from this graph.
     * The node can be specified as an object or as an identifier (string).
     */
    removeNode(nodeOrId) {
        let n = nodeOrId;
        if (isString(nodeOrId)) {
            n = this.getNode(nodeOrId);
        }
        if (isDefined(n)) {
            const links = n.links;
            n.links = [];
            for (let i = 0, len = links.length; i < len; i++) {
                const link = links[i];
                this.removeLink(link);
            }
            this._removeNode(n);
        }
        else {
            throw new Error('The identifier should be a Node or the Id (string) of a node.');
        }
    }
    /**
     * Returns whether the given nodes are connected with a least one link independently of the direction.
     */
    areConnected(n1, n2) {
        return getAny(this.links, function (link) {
            return link.source === n1 && link.target === n2 || link.source === n2 && link.target === n1;
        });
    }
    /**
     * Removes the given link from this graph.
     */
    removeLink(link) {
        /*    if (!this.links.contains(link)) {
            throw "The given link is not part of the Graph.";
            }
            */
        remove(this.links, link);
        remove(link.source.outgoing, link);
        remove(link.source.links, link);
        remove(link.target.incoming, link);
        remove(link.target.links, link);
    }
    /**
     * Adds a new node to this graph, if not already present.
     * The node can be an existing Node or the identifier of a new node.
     * No error is thrown if the node is already there and the existing one is returned.
     */
    addNode(nodeOrId, layoutRect, owner) {
        let newNode = null;
        if (!isDefined(nodeOrId)) {
            throw new Error('No Node or identifier for a new Node is given.');
        }
        if (isString(nodeOrId)) {
            if (this.hasNode(nodeOrId)) {
                return this.getNode(nodeOrId);
            }
            newNode = new Node(nodeOrId);
        }
        else {
            if (this.hasNode(nodeOrId)) {
                return this.getNode(nodeOrId);
            }
            // todo: ensure that the param is a Node?
            newNode = nodeOrId;
        }
        if (isDefined(layoutRect)) {
            newNode.bounds(layoutRect);
        }
        if (isDefined(owner)) {
            newNode.owner = owner;
        }
        this._addNode(newNode);
        return newNode;
    }
    /**
     * Adds the given Node and its outgoing links.
     */
    addNodeAndOutgoings(node) {
        if (!this.hasNode(node)) {
            this._addNode(node);
        }
        const newLinks = node.outgoing;
        node.outgoing = [];
        forEach(newLinks, function (link) {
            this.addExistingLink(link);
        }, this);
    }
    /**
     * Sets the 'index' property on the links and nodes of this graph.
     */
    setItemIndices() {
        let i;
        for (i = 0; i < this.nodes.length; ++i) {
            this.nodes[i].index = i;
        }
        for (i = 0; i < this.links.length; ++i) {
            this.links[i].index = i;
        }
    }
    /**
     * Returns a clone of this graph.
     */
    clone(saveMapping) {
        const copy = new Graph();
        const save = isDefined(saveMapping) && saveMapping === true;
        if (save) {
            copy.nodeMap = new Dictionary();
            copy.linkMap = new Dictionary();
        }
        // we need a map even if the saveMapping is not set
        const map = new Dictionary();
        forEach(this.nodes, function (nOriginal) {
            const nCopy = nOriginal.clone();
            map.set(nOriginal, nCopy);
            copy._addNode(nCopy);
            if (save) {
                copy.nodeMap.set(nCopy, nOriginal);
            }
        });
        forEach(this.links, function (linkOriginal) {
            if (map.containsKey(linkOriginal.source) && map.containsKey(linkOriginal.target)) {
                const linkCopy = copy.addLink(map.get(linkOriginal.source), map.get(linkOriginal.target));
                if (save) {
                    copy.linkMap.set(linkCopy, linkOriginal);
                }
            }
        });
        return copy;
    }
    /**
     * The parsing allows a quick way to create graphs.
     *  - ["n1->n2", "n2->n3"]: creates the three nodes and adds the links
     *  - ["n1->n2", {id: "QSDF"}, "n2->n3"]: same as previous but also performs a deep extend of the link between n1 and n2 with the given object.
     */
    linearize(addIds) {
        return Graph.Utils.linearize(this, addIds);
    }
    /**
     * Performs a depth-first traversal starting at the given node.
     *
     * @param startNode a node or id of a node in this graph
     * @param action
     */
    depthFirstTraversal(startNode, action) {
        if (isUndefined(startNode)) {
            throw new Error('You need to supply a starting node.');
        }
        if (isUndefined(action)) {
            throw new Error('You need to supply an action.');
        }
        if (!this.hasNode(startNode)) {
            throw new Error('The given start-node is not part of this graph');
        }
        const foundNode = this.getNode(startNode); // case the given one is an Id
        const visited = [];
        this._dftIterator(foundNode, action, visited);
    }
    _dftIterator(node, action, visited) {
        action(node);
        visited.push(node);
        const children = node.getChildren();
        for (let i = 0, len = children.length; i < len; i++) {
            const child = children[i];
            if (contains(visited, child)) {
                continue;
            }
            this._dftIterator(child, action, visited);
        }
    }
    /**
     * Performs a breadth-first traversal starting at the given node.
     *
     * @param startNode a node or id of a node in this graph
     * @param action
     */
    breadthFirstTraversal(startNode, action) {
        if (isUndefined(startNode)) {
            throw new Error('You need to supply a starting node.');
        }
        if (isUndefined(action)) {
            throw new Error('You need to supply an action.');
        }
        if (!this.hasNode(startNode)) {
            throw new Error('The given start-node is not part of this graph');
        }
        const foundNode = this.getNode(startNode); // case the given one is an Id
        const queue = new Queue();
        const visited = [];
        queue.enqueue(foundNode);
        while (queue.length > 0) {
            const node = queue.dequeue();
            action(node);
            visited.push(node);
            const children = node.getChildren();
            for (let i = 0, len = children.length; i < len; i++) {
                const child = children[i];
                if (contains(visited, child) || queue.contains(child)) {
                    continue;
                }
                queue.enqueue(child);
            }
        }
    }
    /**
     * This is the classic Tarjan algorithm for strongly connected components.
     * See e.g. http://en.wikipedia.org/wiki/Tarjan's_strongly_connected_components_algorithm
     *
     * @param excludeSingleItems Whether isolated nodes should be excluded from the analysis.
     * @param node The start node from which the analysis starts.
     * @param indices  Numbers the nodes consecutively in the order in which they are discovered.
     * @param lowLinks The smallest index of any node known to be reachable from the node, including the node itself
     * @param connected The current component.
     * @param stack The bookkeeping stack of things to visit.
     * @param index The counter of visited nodes used to assign the indices.
     * @private
     */
    _stronglyConnectedComponents(excludeSingleItems, node, indices, lowLinks, connected, stack, index) {
        indices.add(node, index);
        lowLinks.add(node, index);
        index++;
        stack.push(node);
        const children = node.getChildren();
        let next;
        for (let i = 0, len = children.length; i < len; i++) {
            next = children[i];
            if (!indices.containsKey(next)) {
                this._stronglyConnectedComponents(excludeSingleItems, next, indices, lowLinks, connected, stack, index);
                lowLinks.add(node, Math.min(lowLinks.get(node), lowLinks.get(next)));
            }
            else if (contains(stack, next)) {
                lowLinks.add(node, Math.min(lowLinks.get(node), indices.get(next)));
            }
        }
        // If v is a root node, pop the stack and generate a strong component
        if (lowLinks.get(node) === indices.get(node)) {
            const component = [];
            do {
                next = stack.pop();
                component.push(next);
            } while (next !== node);
            if (!excludeSingleItems || (component.length > 1)) {
                connected.push(component);
            }
        }
    }
    /**
     * Returns the cycles found in this graph.
     * The returned arrays consist of the nodes which are strongly coupled.
     *
     * @param excludeSingleItems Whether isolated nodes should be excluded.
     * @returns {Array} The array of cycles found.
     */
    findCycles(excludeSingleItems) {
        if (isUndefined(excludeSingleItems)) {
            excludeSingleItems = true;
        }
        const indices = new Dictionary();
        const lowLinks = new Dictionary();
        const connected = [];
        const stack = [];
        for (let i = 0, len = this.nodes.length; i < len; i++) {
            const node = this.nodes[i];
            if (indices.containsKey(node)) {
                continue;
            }
            this._stronglyConnectedComponents(excludeSingleItems, node, indices, lowLinks, connected, stack, 0);
        }
        return connected;
    }
    /**
     * Returns whether this graph is acyclic.
     *
     * @returns {*}
     */
    isAcyclic() {
        return isEmpty(this.findCycles());
    }
    /**
     * Returns whether the given graph is a subgraph of this one.
     *
     * @param other Another graph instance.
     */
    isSubGraph(other) {
        const otherArray = other.linearize();
        const thisArray = this.linearize();
        return all(otherArray, function (s) {
            return contains(thisArray, s);
        });
    }
    /**
     *  Makes an acyclic graph from the current (connected) one.
     * * @returns {Array} The reversed links.
     */
    makeAcyclic() {
        // if empty or almost empty
        if (this.isEmpty() || this.nodes.length <= 1 || this.links.length <= 1) {
            return [];
        }
        // singular case of just two nodes
        if (this.nodes.length === 2) {
            const result = [];
            if (this.links.length > 1) {
                const oneLink = this.links[0];
                const oneNode = oneLink.source;
                for (let i = 0, len = this.links.length; i < len; i++) {
                    const link = this.links[i];
                    if (link.source === oneNode) {
                        continue;
                    }
                    const rev = link.reverse();
                    result.push(rev);
                }
            }
            return result;
        }
        const copy = this.clone(true); // copy.nodeMap tells you the mapping
        const N = this.nodes.length;
        const intensityCatalog = new Dictionary();
        /**
         * If there are both incoming and outgoing links this will return the flow intensity (out-in).
         * Otherwise the node acts as a flow source with N specifying the (equal) intensity.
         *
         * @param node
         * @returns {number}
         */
        const flowIntensity = function (node) {
            if (node.outgoing.length === 0) {
                return (2 - N);
            }
            else if (node.incoming.length === 0) {
                return (N - 2);
            }
            else {
                return node.outgoing.length - node.incoming.length;
            }
        };
        /**
         * Collects the nodes with the same intensity.
         *
         * @param node
         * @param intensityCatalog
         */
        const catalogEqualIntensity = function (node) {
            const intensity = flowIntensity(node);
            if (!intensityCatalog.containsKey(intensity)) {
                intensityCatalog.set(intensity, []);
            }
            intensityCatalog.get(intensity).push(node);
        };
        forEach(copy.nodes, function (v) {
            catalogEqualIntensity(v);
        });
        let sourceStack = [];
        const targetStack = [];
        while (copy.nodes.length > 0) {
            let source, target, intensity;
            if (intensityCatalog.containsKey(2 - N)) {
                const targets = intensityCatalog.get(2 - N); // nodes without outgoings
                while (targets.length > 0) {
                    target = targets.pop();
                    for (let li = 0; li < target.links.length; li++) {
                        const targetLink = target.links[li];
                        source = targetLink.getComplement(target);
                        intensity = flowIntensity(source);
                        remove(intensityCatalog.get(intensity), source);
                        source.removeLink(targetLink);
                        catalogEqualIntensity(source);
                    }
                    copy._removeNode(target);
                    targetStack.unshift(target);
                }
            }
            // move sources to sourceStack
            if (intensityCatalog.containsKey(N - 2)) {
                const sources = intensityCatalog.get(N - 2); // nodes without incomings
                while (sources.length > 0) {
                    source = sources.pop();
                    for (let si = 0; si < source.links.length; si++) {
                        const sourceLink = source.links[si];
                        target = sourceLink.getComplement(source);
                        intensity = flowIntensity(target);
                        remove(intensityCatalog.get(intensity), target);
                        target.removeLink(sourceLink);
                        catalogEqualIntensity(target);
                    }
                    sourceStack.push(source);
                    copy._removeNode(source);
                }
            }
            if (copy.nodes.length > 0) {
                for (let k = N - 3; k > 2 - N; k--) {
                    if (intensityCatalog.containsKey(k) &&
                        intensityCatalog.get(k).length > 0) {
                        const maxdiff = intensityCatalog.get(k);
                        const v = maxdiff.pop();
                        for (let ri = 0; ri < v.links.length; ri++) {
                            const ril = v.links[ri];
                            const u = ril.getComplement(v);
                            intensity = flowIntensity(u);
                            remove(intensityCatalog.get(intensity), u);
                            u.removeLink(ril);
                            catalogEqualIntensity(u);
                        }
                        sourceStack.push(v);
                        copy._removeNode(v);
                        break;
                    }
                }
            }
        }
        sourceStack = sourceStack.concat(targetStack);
        const vertexOrder = new Dictionary();
        for (let kk = 0; kk < this.nodes.length; kk++) {
            vertexOrder.set(copy.nodeMap.get(sourceStack[kk]), kk);
        }
        const reversedEdges = [];
        forEach(this.links, function (link) {
            if (vertexOrder.get(link.source) > vertexOrder.get(link.target)) {
                link.reverse();
                reversedEdges.push(link);
            }
        });
        return reversedEdges;
    }
}
/**
 * A collection of predefined graphs for demo and testing purposes.
 */
Graph.Predefined = {
    /**
     * Eight-shapes graph all connected in a cycle.
     *
     * @returns {*}
     * @constructor
     */
    EightGraph() {
        return Graph.Utils.parse(['1->2', '2->3', '3->4', '4->1', '3->5', '5->6', '6->7', '7->3']);
    },
    /**
     * Creates a typical mindmap diagram.
     *
     * @returns {*}
     * @constructor
     */
    Mindmap() {
        return Graph.Utils.parse(['0->1', '0->2', '0->3', '0->4', '0->5', '1->6', '1->7', '7->8', '2->9', '9->10', '9->11', '3->12',
            '12->13', '13->14', '4->15', '4->16', '15->17', '15->18', '18->19', '18->20', '14->21', '14->22', '5->23', '23->24', '23->25', '6->26']);
    },
    /**
     * Three nodes connected in a cycle.
     *
     * @returns {*}
     * @constructor
     */
    ThreeGraph() {
        return Graph.Utils.parse(['1->2', '2->3', '3->1']);
    },
    /**
     * A tree with each node having two children.
     *
     * @param levels How many levels the binary tree should have.
     * @returns {diagram.Graph}
     * @constructor
     */
    BinaryTree(levels) {
        if (isUndefined(levels)) {
            levels = 5;
        }
        return Graph.Utils.createBalancedTree(levels, 2);
    },
    /**
     * A linear graph (discrete line segment).
     *
     * @param length How many segments (the node count is hence (length+1)).
     * @returns {diagram.Graph}
     * @constructor
     */
    Linear(length) {
        if (isUndefined(length)) {
            length = 10;
        }
        return Graph.Utils.createBalancedTree(length, 1);
    },
    /**
     * A standard tree-graph with the specified levels and children (siblings) count.
     * Note that for a balanced tree of level N and sibling count s, counting the root as level zero:
     * - NodeCount = (1-s^(N+1))/(1-s)]
     * - LinkCount = s.(1-s^N)/(1-s)
     *
     * @param levels How many levels the tree should have.
     * @param siblingsCount How many siblings each level should have.
     * @returns {diagram.Graph}
     * @constructor
     */
    Tree(levels, siblingsCount) {
        return Graph.Utils.createBalancedTree(levels, siblingsCount);
    },
    /**
     * Creates a forest.
     * Note that for a balanced forest of level N, sibling count s and tree count t, counting the root as level zero:
     * - NodeCount = t.(1-s^(N+1))/(1-s)]
     * - LinkCount = t.s.(1-s^N)/(1-s)
     *
     * @param levels How many levels the tree should have.
     * @param siblingsCount How many siblings each level should have.
     * @param trees The amount of trees the forest should have.
     * @returns {diagram.Graph}
     * @constructor
     */
    Forest(levels, siblingsCount, trees) {
        return Graph.Utils.createBalancedForest(levels, siblingsCount, trees);
    },
    /**
     * A workflow-like graph with cycles.
     *
     * @returns {*}
     * @constructor
     */
    Workflow() {
        return Graph.Utils.parse(['0->1', '1->2', '2->3', '1->4', '4->3', '3->5', '5->6', '6->3', '6->7', '5->4']);
    },
    /**
     * A grid graph with the direction of the links avoiding cycles.
     * Node count: (n+1).(m+1)
     * Link count: n.(m+1) + m.(n+1)
     *
     * @param n Horizontal count of grid cells. If zero this will result in a linear graph.
     * @param m Vertical count of grid cells. If zero this will result in a linear graph.
     * @constructor
     */
    Grid(n, m) {
        const g = new Graph();
        if (n <= 0 && m <= 0) {
            return g;
        }
        for (let i = 0; i < n + 1; i++) {
            let previous = null;
            for (let j = 0; j < m + 1; j++) {
                // using x-y coordinates to name the nodes
                const node = new Node(i.toString() + '.' + j.toString());
                g.addNode(node);
                if (previous) {
                    g.addLink(previous, node);
                }
                if (i > 0) {
                    const left = g.getNode((i - 1).toString() + '.' + j.toString());
                    g.addLink(left, node);
                }
                previous = node;
            }
        }
        return g;
    }
};
/**
 * Graph generation and other utilities.
 */
Graph.Utils = {
    /**
     * The parsing allows a quick way to create graphs.
     *  - ["n1->n2", "n2->n3"]: creates the three nodes and adds the links
     *  - ["n1->n2", {id: "id177"}, "n2->n3"]: same as previous but also performs a deep extend of the link between n1 and n2 with the given object.
     */
    parse(graphString) {
        let previousLink;
        const graph = new Graph(), parts = graphString.slice();
        for (let i = 0, len = parts.length; i < len; i++) {
            const part = parts[i];
            if (isString(part)) { // link spec
                if (part.indexOf('->') < 0) {
                    throw new Error('The link should be specified as \'a->b\'.');
                }
                const p = part.split('->');
                if (p.length !== 2) {
                    throw new Error('The link should be specified as \'a->b\'.');
                }
                previousLink = new Link(p[0], p[1]);
                graph.addLink(previousLink);
            }
            if (isObject(part)) {
                if (!previousLink) {
                    throw new Error('Specification found before Link definition.');
                }
                deepExtend(previousLink, part);
            }
        }
        return graph;
    },
    /**
     * Returns a linearized representation of the given Graph.
     * See also the Graph.Utils.parse method for the inverse operation.
     */
    linearize(graph, addIds) {
        if (isUndefined(graph)) {
            throw new Error('Expected an instance of a Graph object in slot one.');
        }
        if (isUndefined(addIds)) {
            addIds = false;
        }
        const lin = [];
        for (let i = 0, len = graph.links.length; i < len; i++) {
            const link = graph.links[i];
            lin.push(link.source.id + '->' + link.target.id);
            if (addIds) {
                lin.push({ id: link.id });
            }
        }
        return lin;
    },
    /**
     * The method used by the diagram creation to instantiate a shape.
     *
     * @param kendoDiagram The Kendo diagram where the diagram will be created.
     * @param p The position at which to place the shape.
     * @param shapeDefaults Optional Shape options.
     * @param id Optional identifier of the shape.
     * @returns {*}
     * @private
     */
    _addShape(kendoDiagram, p, id, shapeDefaults) {
        if (isUndefined(p)) {
            p = new Point(0, 0);
        }
        if (isUndefined(id)) {
            id = randomId();
        }
        shapeDefaults = deepExtend({
            width: 20,
            height: 20,
            id: id,
            radius: 10,
            fill: '#778899',
            data: 'circle',
            undoable: false,
            x: p.x,
            y: p.y
        }, shapeDefaults);
        return kendoDiagram.addShape(shapeDefaults);
    },
    /**
     * The method used by the diagram creation to instantiate a connection.
     *
     * @param diagram he Kendo diagram where the diagram will be created.
     * @param from The source shape.
     * @param to The target shape.
     * @param options Optional Connection options.
     * @returns {*}
     * @private
     */
    _addConnection(diagram, from, to, options) {
        return diagram.connect(from, to, options);
    },
    /**
     * Creates a diagram from the given Graph.
     *
     * @param diagram The Kendo diagram where the diagram will be created.
     * @param graph The graph structure defining the diagram.
     */
    createDiagramFromGraph(diagram, graph, doLayout, randomSize) {
        if (isUndefined(diagram)) {
            throw new Error('The diagram surface is undefined.');
        }
        if (isUndefined(graph)) {
            throw new Error('No graph specification defined.');
        }
        if (isUndefined(doLayout)) {
            doLayout = true;
        }
        if (isUndefined(randomSize)) {
            randomSize = false;
        }
        const width = diagram.element.clientWidth || 200;
        const height = diagram.element.clientHeight || 200;
        const map = [];
        let node, shape;
        for (let i = 0, len = graph.nodes.length; i < len; i++) {
            node = graph.nodes[i];
            let p = node.position;
            if (isUndefined(p)) {
                if (isDefined(node.x) && isDefined(node.y)) {
                    p = new Point(node.x, node.y);
                }
                else {
                    p = new Point(randomInteger(10, width - 20), randomInteger(10, height - 20));
                }
            }
            const opt = {};
            if (node.id === '0') ;
            else if (randomSize) {
                deepExtend(opt, {
                    width: Math.random() * 150 + 20,
                    height: Math.random() * 80 + 50,
                    data: 'rectangle',
                    fill: {
                        color: '#778899'
                    }
                });
            }
            shape = this._addShape(diagram, p, node.id, opt);
            // shape.content(node.id);
            const bounds = shape.bounds();
            if (isDefined(bounds)) {
                node.x = bounds.x;
                node.y = bounds.y;
                node.width = bounds.width;
                node.height = bounds.height;
            }
            map[node.id] = shape;
        }
        for (let gli = 0; gli < graph.links.length; gli++) {
            const link = graph.links[gli];
            const sourceShape = map[link.source.id];
            if (isUndefined(sourceShape)) {
                continue;
            }
            const targetShape = map[link.target.id];
            if (isUndefined(targetShape)) {
                continue;
            }
            this._addConnection(diagram, sourceShape, targetShape, { id: link.id });
        }
        if (doLayout) {
            const l = new SpringLayout(diagram);
            l.layoutGraph(graph, { limitToView: false });
            for (let shi = 0; shi < graph.nodes.length; shi++) {
                node = graph.nodes[shi];
                shape = map[node.id];
                shape.bounds(new Rect(node.x, node.y, node.width, node.height));
            }
        }
    },
    /**
     * Creates a balanced tree with the specified number of levels and siblings count.
     * Note that for a balanced tree of level N and sibling count s, counting the root as level zero:
     * - NodeCount = (1-s^(N+1))/(1-s)]
     * - LinkCount = s.(1-s^N)/(1-s)
     *
     * @param levels How many levels the tree should have.
     * @param siblingsCount How many siblings each level should have.
     * @returns {diagram.Graph}
     */
    createBalancedTree(levels, siblingsCount) {
        if (isUndefined(levels)) {
            levels = 3;
        }
        if (isUndefined(siblingsCount)) {
            siblingsCount = 3;
        }
        const g = new Graph();
        let news, counter = -1, lastAdded = [];
        if (levels <= 0 || siblingsCount <= 0) {
            return g;
        }
        const root = new Node((++counter).toString());
        g.addNode(root);
        g.root(root);
        lastAdded.push(root);
        for (let i = 0; i < levels; i++) {
            news = [];
            for (let j = 0; j < lastAdded.length; j++) {
                const parent = lastAdded[j];
                for (let k = 0; k < siblingsCount; k++) {
                    const item = new Node((++counter).toString());
                    g.addLink(parent, item);
                    news.push(item);
                }
            }
            lastAdded = news;
        }
        return g;
    },
    /**
     * Creates a balanced tree with the specified number of levels and siblings count.
     * Note that for a balanced forest of level N, sibling count s and tree count t, counting the root as level zero:
     * - NodeCount = t.(1-s^(N+1))/(1-s)]
     * - LinkCount = t.s.(1-s^N)/(1-s)
     *
     * @param levels How many levels the tree should have.
     * @param siblingsCount How many siblings each level should have.
     * @returns {diagram.Graph}
     * @param treeCount The number of trees the forest should have.
     */
    createBalancedForest(levels, siblingsCount, treeCount) {
        if (isUndefined(levels)) {
            levels = 3;
        }
        if (isUndefined(siblingsCount)) {
            siblingsCount = 3;
        }
        if (isUndefined(treeCount)) {
            treeCount = 5;
        }
        const g = new Graph();
        let counter = -1, lastAdded = [], news;
        if (levels <= 0 || siblingsCount <= 0 || treeCount <= 0) {
            return g;
        }
        for (let t = 0; t < treeCount; t++) {
            const root = new Node((++counter).toString());
            g.addNode(root);
            lastAdded = [root];
            for (let i = 0; i < levels; i++) {
                news = [];
                for (let j = 0; j < lastAdded.length; j++) {
                    const parent = lastAdded[j];
                    for (let k = 0; k < siblingsCount; k++) {
                        const item = new Node((++counter).toString());
                        g.addLink(parent, item);
                        news.push(item);
                    }
                }
                lastAdded = news;
            }
        }
        return g;
    },
    /**
     * Creates a random graph (uniform distribution) with the specified amount of nodes.
     *
     * @param nodeCount The amount of nodes the random graph should have.
     * @param maxIncidence The maximum allowed degree of the nodes.
     * @param isTree Whether the return graph should be a tree (default: false).
     * @returns {diagram.Graph}
     */
    createRandomConnectedGraph(nodeCount, maxIncidence, isTree) {
        /* Swa's Mathematica export of random Bernoulli graphs
           gr[n_,p_]:=Module[{g=RandomGraph[BernoulliGraphDistribution[n,p],VertexLabels->"Name",DirectedEdges->True]},
           While[Not[ConnectedGraphQ[g]],g=RandomGraph[BernoulliGraphDistribution[n,p],VertexLabels->"Name",DirectedEdges->True]];g];
           project[a_]:=("\""<>ToString[Part[#,1]]<>"->"<>ToString[Part[#,2]]<>"\"")&     @ a;
           export[g_]:=project/@ EdgeList[g]
           g = gr[12,.1]
           export [g]
           */
        if (isUndefined(nodeCount)) {
            nodeCount = 40;
        }
        if (isUndefined(maxIncidence)) {
            maxIncidence = 4;
        }
        if (isUndefined(isTree)) {
            isTree = false;
        }
        const g = new Graph();
        let counter = -1;
        if (nodeCount <= 0) {
            return g;
        }
        const root = new Node((++counter).toString());
        g.addNode(root);
        if (nodeCount === 1) {
            return g;
        }
        if (nodeCount > 1) {
            // random tree
            for (let i = 1; i < nodeCount; i++) {
                const poolNode = g.takeRandomNode([], maxIncidence);
                if (!poolNode) {
                    // failed to find one so the graph will have less nodes than specified
                    break;
                }
                const newNode = g.addNode(i.toString());
                g.addLink(poolNode, newNode);
            }
            if (!isTree && nodeCount > 1) {
                const randomAdditions = randomInteger(1, nodeCount);
                for (let ri = 0; ri < randomAdditions; ri++) {
                    const n1 = g.takeRandomNode([], maxIncidence);
                    const n2 = g.takeRandomNode([], maxIncidence);
                    if (n1 && n2 && !g.areConnected(n1, n2)) {
                        g.addLink(n1, n2);
                    }
                }
            }
            return g;
        }
    },
    /**
     * Generates a random diagram.
     *
     * @param diagram The host diagram.
     * @param shapeCount The number of shapes the random diagram should contain.
     * @param maxIncidence The maximum degree the shapes can have.
     * @param isTree Whether the generated diagram should be a tree
     * @param layoutType The optional layout type to apply after the diagram is generated.
     */
    randomDiagram(diagram, shapeCount, maxIncidence, isTree, randomSize) {
        const g = Graph.Utils.createRandomConnectedGraph(shapeCount, maxIncidence, isTree);
        Graph.Utils.createDiagramFromGraph(diagram, g, false, randomSize);
    }
};
/**
 * The classic spring-embedder (aka force-directed, Fruchterman-Rheingold, barycentric) algorithm.
 * http://en.wikipedia.org/wiki/Force-directed_graph_drawing
 * - Chapter 12 of Tamassia et al. "Handbook of graph drawing and visualization".
 * - Kobourov on preprint arXiv; http://arxiv.org/pdf/1201.3011.pdf
 * - Fruchterman and Rheingold in SOFTWARE-PRACTICE AND EXPERIENCE, VOL. 21(1 1), 1129-1164 (NOVEMBER 1991)
 *
 * @type {*}
 */
class SpringLayout extends LayoutBase {
    constructor(diagram) {
        super();
        if (isUndefined(diagram)) {
            throw new Error('Diagram is not specified.');
        }
        this.diagram = diagram;
    }
    layout(options) {
        this.transferOptions(options);
        const adapter = new DiagramToHyperTreeAdapter(this.diagram);
        const graph = adapter.convert(options);
        if (graph.isEmpty()) {
            return;
        }
        // split into connected components
        const components = graph.getConnectedComponents();
        if (isEmpty(components)) {
            return;
        }
        for (let i = 0; i < components.length; i++) {
            const component = components[i];
            this.layoutGraph(component, options);
        }
        const finalNodeSet = this.gridLayoutComponents(components);
        return new LayoutState(this.diagram, finalNodeSet);
    }
    layoutGraph(graph, options) {
        if (isDefined(options)) {
            this.transferOptions(options);
        }
        this.graph = graph;
        const initialTemperature = this.options.nodeDistance * 9;
        this.temperature = initialTemperature;
        const guessBounds = this._expectedBounds();
        this.width = guessBounds.width;
        this.height = guessBounds.height;
        for (let step = 0; step < this.options.iterations; step++) {
            this.refineStage = step >= this.options.iterations * 5 / 6;
            this.tick();
            // exponential cooldown
            this.temperature = this.refineStage ?
                initialTemperature / 30 :
                initialTemperature * (1 - step / (2 * this.options.iterations));
        }
    }
    /**
     * Single iteration of the simulation.
     */
    tick() {
        let i;
        // collect the repulsive forces on each node
        for (i = 0; i < this.graph.nodes.length; i++) {
            this._repulsion(this.graph.nodes[i]);
        }
        // collect the attractive forces on each node
        for (i = 0; i < this.graph.links.length; i++) {
            this._attraction(this.graph.links[i]);
        }
        // update the positions
        for (i = 0; i < this.graph.nodes.length; i++) {
            const node = this.graph.nodes[i];
            const offset = Math.sqrt(node.dx * node.dx + node.dy * node.dy);
            if (offset === 0) {
                return;
            }
            node.x += Math.min(offset, this.temperature) * node.dx / offset;
            node.y += Math.min(offset, this.temperature) * node.dy / offset;
            if (this.options.limitToView) {
                node.x = Math.min(this.width, Math.max(node.width / 2, node.x));
                node.y = Math.min(this.height, Math.max(node.height / 2, node.y));
            }
        }
    }
    /**
     * Shakes the node away from its current position to escape the deadlock.
     *
     * @param node A Node.
     * @private
     */
    _shake(node) {
        // just a simple polar neighborhood
        const rho = Math.random() * this.options.nodeDistance / 4;
        const alpha = Math.random() * 2 * Math.PI;
        node.x += rho * Math.cos(alpha);
        node.y -= rho * Math.sin(alpha);
    }
    /**
     * The typical Coulomb-Newton force law F=k/r^2
     *
     * @remark This only works in dimensions less than three.
     * @param d
     * @param n A Node.
     * @param m Another Node.
     * @returns {number}
     * @private
     */
    _InverseSquareForce(d, n, m) {
        let force;
        if (!this.refineStage) {
            force = Math.pow(d, 2) / Math.pow(this.options.nodeDistance, 2);
        }
        else {
            const deltax = n.x - m.x;
            const deltay = n.y - m.y;
            const wn = n.width / 2;
            const hn = n.height / 2;
            const wm = m.width / 2;
            const hm = m.height / 2;
            force = (Math.pow(deltax, 2) / Math.pow(wn + wm + this.options.nodeDistance, 2)) + (Math.pow(deltay, 2) / Math.pow(hn + hm + this.options.nodeDistance, 2));
        }
        return force * 4 / 3;
    }
    /**
     * The typical Hooke force law F=kr^2
     *
     * @param d
     * @param n
     * @param m
     * @returns {number}
     * @private
     */
    _SquareForce(d, n, m) {
        return 1 / this._InverseSquareForce(d, n, m);
    }
    _repulsion(n) {
        n.dx = 0;
        n.dy = 0;
        forEach(this.graph.nodes, function (m) {
            if (m === n) {
                return;
            }
            while (n.x === m.x && n.y === m.y) {
                this._shake(m);
            }
            const vx = n.x - m.x;
            const vy = n.y - m.y;
            const distance = Math.sqrt(vx * vx + vy * vy);
            const r = this._SquareForce(distance, n, m) * 2;
            n.dx += (vx / distance) * r;
            n.dy += (vy / distance) * r;
        }, this);
    }
    _attraction(link) {
        const t = link.target;
        const s = link.source;
        if (s === t) {
            // loops induce endless shakes
            return;
        }
        while (s.x === t.x && s.y === t.y) {
            this._shake(t);
        }
        const vx = s.x - t.x;
        const vy = s.y - t.y;
        const distance = Math.sqrt(vx * vx + vy * vy);
        const a = this._InverseSquareForce(distance, s, t) * 5;
        const dx = (vx / distance) * a;
        const dy = (vy / distance) * a;
        t.dx += dx;
        t.dy += dy;
        s.dx -= dx;
        s.dy -= dy;
    }
    /**
     * Calculates the expected bounds after layout.
     *
     * @returns {*}
     * @private
     */
    _expectedBounds() {
        const N = this.graph.nodes.length, /* golden ration optimal?*/ ratio = 1.5, multiplier = 4;
        if (N === 0) {
            return;
        }
        const size = fold(this.graph.nodes, function (s, node) {
            const area = node.width * node.height;
            if (area > 0) {
                s += Math.sqrt(area);
                return s;
            }
            return 0;
        }, 0, this);
        const av = size / N;
        const squareSize = av * Math.ceil(Math.sqrt(N));
        const width = squareSize * Math.sqrt(ratio);
        const height = squareSize / Math.sqrt(ratio);
        return { width: width * multiplier, height: height * multiplier };
    }
}
/**
 * Adapter between the diagram control and the graph representation. It converts shape and connections to nodes and edges taking into the containers and their collapsef state,
 * the visibility of items and more. If the layoutContainerChildren is true a hypertree is constructed which holds the hierarchy of containers and many conditions are analyzed
 * to investigate how the effective graph structure looks like and how the layout has to be performed.
 *
 * @type {*}
 */
class DiagramToHyperTreeAdapter {
    constructor(diagram) {
        /**
         * The mapping to/from the original nodes.
         *
         * @type {Dictionary}
         */
        this.nodeMap = new Dictionary();
        /**
         * Gets the mapping of a shape to a container in case the shape sits in a collapsed container.
         *
         * @type {Dictionary}
         */
        this.shapeMap = new Dictionary();
        /**
         * The nodes being mapped.
         *
         * @type {Dictionary}
         */
        this.nodes = [];
        /**
         * The connections being mapped.
         *
         * @type {Dictionary}
         */
        this.edges = [];
        // the mapping from an edge to all the connections it represents, this can be both because of multiple connections between
        // two shapes or because a container holds multiple connections to another shape or container.
        this.edgeMap = new Dictionary();
        /**
         * The resulting set of Nodes when the analysis has finished.
         *
         * @type {Array}
         */
        this.finalNodes = [];
        /**
         * The resulting set of Links when the analysis has finished.
         *
         * @type {Array}
         */
        this.finalLinks = [];
        /**
         * The items being omitted because of multigraph edges.
         *
         * @type {Array}
         */
        this.ignoredConnections = [];
        /**
         * The items being omitted because of containers, visibility and other factors.
         *
         * @type {Array}
         */
        this.ignoredShapes = [];
        /**
         * The map from a node to the partition/hypernode in which it sits. This hyperMap is null if 'options.layoutContainerChildren' is false.
         *
         * @type {Dictionary}
         */
        this.hyperMap = new Dictionary();
        /**
         * The hypertree contains the hierarchy defined by the containers.
         * It's in essence a Graph of Graphs with a tree structure defined by the hierarchy of containers.
         *
         * @type {HyperTree}
         */
        this.hyperTree = new Graph();
        /**
         * The resulting graph after conversion. Note that this does not supply the information contained in the
         * ignored connection and shape collections.
         *
         * @type {null}
         */
        this.finalGraph = null;
        this.diagram = diagram;
    }
    /**
     * The hyperTree is used when the 'options.layoutContainerChildren' is true. It contains the hierarchy of containers whereby each node is a ContainerGraph.
     * This type of node has a Container reference to the container which holds the Graph items. There are three possible situations during the conversion process:
     * - Ignore the containers: the container are non-existent and only normal shapes are mapped. If a shape has a connection to a container it will be ignored as well
     * since there is no node mapped for the container.
     * - Do not ignore the containers and leave the content of the containers untouched: the top-level elements are being mapped and the children within a container are not altered.
     * - Do not ignore the containers and organize the content of the containers as well: the hypertree is constructed and there is a partitioning of all nodes and connections into the hypertree.
     * The only reason a connection or node is not being mapped might be due to the visibility, which includes the visibility change through a collapsed parent container.
     *
     * @param options
     */
    convert(options) {
        if (isUndefined(this.diagram)) {
            throw new Error('No diagram to convert.');
        }
        this.options = deepExtend({
            ignoreInvisible: true,
            ignoreContainers: true,
            layoutContainerChildren: false
        }, options || {});
        this.clear();
        // create the nodes which participate effectively in the graph analysis
        this._renormalizeShapes();
        // recreate the incoming and outgoing collections of each and every node
        this._renormalizeConnections();
        // export the resulting graph
        this.finalNodes = new Dictionary(this.nodes);
        this.finalLinks = new Dictionary(this.edges);
        this.finalGraph = new Graph();
        this.finalNodes.forEach(function (n) {
            this.finalGraph.addNode(n);
        }, this);
        this.finalLinks.forEach(function (l) {
            this.finalGraph.addExistingLink(l);
        }, this);
        return this.finalGraph;
    }
    /**
     * Maps the specified connection to an edge of the graph deduced from the given diagram.
     *
     * @param connection
     * @returns {*}
     */
    mapConnection(connection) {
        return this.edgeMap.get(connection.id);
    }
    /**
     * Maps the specified shape to a node of the graph deduced from the given diagram.
     *
     * @param shape
     * @returns {*}
     */
    mapShape(shape) {
        return this.nodeMap.get(shape.id);
    }
    /**
     * Gets the edge, if any, between the given nodes.
     *
     * @param a
     * @param b
     */
    getEdge(a, b) {
        return first(a.links, function (link) {
            return link.getComplement(a) === b;
        });
    }
    /**
     * Clears all the collections used by the conversion process.
     */
    clear() {
        this.finalGraph = null;
        this.hyperTree = (!this.options.ignoreContainers && this.options.layoutContainerChildren) ? new Graph() : null;
        this.hyperMap = (!this.options.ignoreContainers && this.options.layoutContainerChildren) ? new Dictionary() : null;
        this.nodeMap = new Dictionary();
        this.shapeMap = new Dictionary();
        this.nodes = [];
        this.edges = [];
        this.edgeMap = new Dictionary();
        this.ignoredConnections = [];
        this.ignoredShapes = [];
        this.finalNodes = [];
        this.finalLinks = [];
    }
    /**
     * The path from a given ContainerGraph to the root (container).
     *
     * @param containerGraph
     * @returns {Array}
     */
    listToRoot(containerGraph) {
        const list = [];
        let s = containerGraph.container;
        if (!s) {
            return list;
        }
        list.push(s);
        while (s.parentContainer) {
            s = s.parentContainer;
            list.push(s);
        }
        list.reverse();
        return list;
    }
    firstNonIgnorableContainer(shape) {
        if (shape.isContainer && !this.isIgnorableItem(shape)) {
            return shape;
        }
        return !shape.parentContainer ? null : this.firstNonIgnorableContainer(shape.parentContainer);
    }
    isContainerConnection(a, b) {
        if (a.isContainer && this.isDescendantOf(a, b)) {
            return true;
        }
        return b.isContainer && this.isDescendantOf(b, a);
    }
    /**
     * Returns true if the given shape is a direct child or a nested container child of the given container.
     * If the given container and shape are the same this will return false since a shape cannot be its own child.
     *
     * @param scope
     * @param a
     * @returns {boolean}
     */
    isDescendantOf(scope, a) {
        if (!scope.isContainer) {
            throw new Error('Expecting a container.');
        }
        if (scope === a) {
            return false;
        }
        if (contains(scope.children, a)) {
            return true;
        }
        const containers = [];
        for (let i = 0, len = scope.children.length; i < len; i++) {
            const c = scope.children[i];
            if (c.isContainer && this.isDescendantOf(c, a)) {
                containers.push(c);
            }
        }
        return containers.length > 0;
    }
    isIgnorableItem(shape) {
        if (this.options.ignoreInvisible) {
            if (shape.isCollapsed && this._isVisible(shape)) {
                return false;
            }
            if (!shape.isCollapsed && this._isVisible(shape)) {
                return false;
            }
            return true;
        }
        else {
            return shape.isCollapsed && !this._isTop(shape);
        }
    }
    /**
     *  Determines whether the shape is or needs to be mapped to another shape. This occurs essentially when the shape sits in
     *  a collapsed container hierarchy and an external connection needs a node endpoint. This node then corresponds to the mapped shape and is
     *  necessarily a container in the parent hierarchy of the shape.
     *
     * @param shape
     */
    isShapeMapped(shape) {
        return shape.isCollapsed && !this._isVisible(shape) && !this._isTop(shape);
    }
    leastCommonAncestor(a, b) {
        if (!a) {
            throw new Error('Parameter should not be null.');
        }
        if (!b) {
            throw new Error('Parameter should not be null.');
        }
        if (!this.hyperTree) {
            throw new Error('No hypertree available.');
        }
        const al = this.listToRoot(a);
        const bl = this.listToRoot(b);
        let found = null;
        if (isEmpty(al) || isEmpty(bl)) {
            return this.hyperTree.root().data;
        }
        let xa = al[0];
        let xb = bl[0];
        let i = 0;
        while (xa === xb) {
            found = al[i];
            i++;
            if (i >= al.length || i >= bl.length) {
                break;
            }
            xa = al[i];
            xb = bl[i];
        }
        if (!found) {
            return this.hyperTree.root().data;
        }
        else {
            return this.hyperTree.nodes.filter(function (n) {
                return n.data.container === found;
            });
        }
    }
    /**
     * Determines whether the specified item is a top-level shape or container.
     *
     * @param item
     * @returns {boolean}
     * @private
     */
    _isTop(item) {
        return !item.parentContainer;
    }
    /**
     * Determines iteratively (by walking up the container stack) whether the specified shape is visible.
     * This does NOT tell whether the item is not visible due to an explicit Visibility change or due to a collapse state.
     *
     * @param shape
     * @returns {*}
     * @private
     */
    _isVisible(shape) {
        if (!shape.visible()) {
            return false;
        }
        return !shape.parentContainer ? shape.visible() : this._isVisible(shape.parentContainer);
    }
    _isCollapsed(shape) {
        if (shape.isContainer && shape.isCollapsed) {
            return true;
        }
        return shape.parentContainer && this._isCollapsed(shape.parentContainer);
    }
    /**
     * First part of the graph creation; analyzing the shapes and containers and deciding whether they should be mapped to a Node.
     *
     * @private
     */
    _renormalizeShapes() {
        // add the nodes, the adjacency structure will be reconstructed later on
        if (this.options.ignoreContainers) {
            for (let i = 0, len = this.diagram.shapes.length; i < len; i++) {
                const shape = this.diagram.shapes[i];
                // if not visible (and ignoring the invisible ones) or a container we skip
                if ((this.options.ignoreInvisible && !this._isVisible(shape)) || shape.isContainer) {
                    this.ignoredShapes.push(shape);
                    continue;
                }
                const node = new Node(shape.id, shape);
                node.isVirtual = false;
                // the mapping will always contain singletons and the hyperTree will be null
                this.nodeMap.add(shape.id, node);
                this.nodes.push(node);
            }
        }
        else {
            throw new Error('Containers are not supported yet, but stay tuned.');
        }
    }
    /**
     * Second part of the graph creation; analyzing the connections and deciding whether they should be mapped to an edge.
     *
     * @private
     */
    _renormalizeConnections() {
        if (this.diagram.connections.length === 0) {
            return;
        }
        for (let i = 0, len = this.diagram.connections.length; i < len; i++) {
            const conn = this.diagram.connections[i];
            if (this.isIgnorableItem(conn)) {
                this.ignoredConnections.push(conn);
                continue;
            }
            let source = !conn.sourceConnector ? null : conn.sourceConnector.shape;
            let sink = !conn.targetConnector ? null : conn.targetConnector.shape;
            // no layout for floating connections
            if (!source || !sink) {
                this.ignoredConnections.push(conn);
                continue;
            }
            if (contains(this.ignoredShapes, source) && !this.shapeMap.containsKey(source)) {
                this.ignoredConnections.push(conn);
                continue;
            }
            if (contains(this.ignoredShapes, sink) && !this.shapeMap.containsKey(sink)) {
                this.ignoredConnections.push(conn);
                continue;
            }
            // if the endpoint sits in a collapsed container we need the container rather than the shape itself
            if (this.shapeMap.containsKey(source)) {
                source = this.shapeMap[source];
            }
            if (this.shapeMap.containsKey(sink)) {
                sink = this.shapeMap[sink];
            }
            const sourceNode = this.mapShape(source);
            const sinkNode = this.mapShape(sink);
            if ((sourceNode === sinkNode) || this.areConnectedAlready(sourceNode, sinkNode)) {
                this.ignoredConnections.push(conn);
                continue;
            }
            if (sourceNode === null || sinkNode === null) {
                throw new Error('A shape was not mapped to a node.');
            }
            if (this.options.ignoreContainers) {
                // much like a floating connection here since at least one end is attached to a container
                if (sourceNode.isVirtual || sinkNode.isVirtual) {
                    this.ignoredConnections.push(conn);
                    continue;
                }
                const newEdge = new Link(sourceNode, sinkNode, conn.id, conn);
                this.edgeMap.add(conn.id, newEdge);
                this.edges.push(newEdge);
            }
            else {
                throw new Error('Containers are not supported yet, but stay tuned.');
            }
        }
    }
    areConnectedAlready(n, m) {
        return getAny(this.edges, function (l) {
            return l.source === n && l.target === m || l.source === m && l.target === n;
        });
    }
}

export { DiagramToHyperTreeAdapter, Graph, Link, Node, SpringLayout };
